import { useMemo, useRef, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { Hotspot } from "./thermal/Hotspot";

const W = 5.4;
const F = 3.1;
const yOf = (x, z) => (x * x + z * z) / (4 * F);
const H = W / 2;
const COL_TOP = 2.9;
const DISH_LIFT = 0.28;
const RAY_PTS = [[-1.9, -1.9], [0, -1.9], [1.9, -1.9], [-1.9, 0], [1.9, 0], [-1.9, 1.9], [0, 1.9], [1.9, 1.9], [0.9, -0.9], [-0.9, 0.9]];

const STEEL = { color: "#aeb5bb", metalness: 0.85, roughness: 0.35 };

function Tube({ points, r = 0.05, mat = STEEL, segs = 24 }) {
  const geo = useMemo(() => {
    const curve = new THREE.CatmullRomCurve3(points.map((p) => new THREE.Vector3(...p)));
    return new THREE.TubeGeometry(curve, segs, r, 8, false);
  }, [points, r, segs]);
  return (
    <mesh geometry={geo}>
      <meshStandardMaterial {...mat} />
    </mesh>
  );
}

function Strut({ from, to, r = 0.04 }) {
  return <Tube points={[from, to]} r={r} segs={1} />;
}

function edgeCurve(axis, side) {
  const pts = [];
  for (let i = 0; i <= 16; i++) {
    const t = -H + (i / 16) * W;
    pts.push(axis === "x" ? [t, yOf(t, side) - 0.07, side] : [side, yOf(side, t) - 0.07, t]);
  }
  return pts;
}

function Mirrors({ selected }) {
  const geo = useMemo(() => {
    const NX = 24, NZ = 10, GX = 0.035, GZ = 0.02;
    const pos = [];
    const sx = W / NX, sz = W / NZ;
    for (let i = 0; i < NX; i++) {
      for (let j = 0; j < NZ; j++) {
        const x0 = -H + i * sx + GX / 2, x1 = -H + (i + 1) * sx - GX / 2;
        const z0 = -H + j * sz + GZ / 2, z1 = -H + (j + 1) * sz - GZ / 2;
        const a = [x0, yOf(x0, z0), z0], b = [x1, yOf(x1, z0), z0], c = [x1, yOf(x1, z1), z1], d = [x0, yOf(x0, z1), z1];
        pos.push(...a, ...b, ...c, ...a, ...c, ...d);
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    g.computeVertexNormals();
    return g;
  }, []);
  return (
    <mesh geometry={geo}>
      <meshStandardMaterial color="#f4f7fa" metalness={1} roughness={0.06} envMapIntensity={1.4} side={THREE.DoubleSide} emissive="#00ff88" emissiveIntensity={selected ? 0.17 : 0} />
    </mesh>
  );
}

function Frame() {
  const edges = useMemo(() => [edgeCurve("x", -H), edgeCurve("x", H), edgeCurve("z", -H), edgeCurve("z", H)], []);
  const ribs = useMemo(() => [-1.8, -0.6, 0.6, 1.8].flatMap((s) => [edgeCurve("x", s).map((p) => [p[0], p[1] - 0.06, p[2]]), edgeCurve("z", s).map((p) => [p[0], p[1] - 0.06, p[2]])]), []);
  const corners = [[-H, yOf(H, H), -H], [H, yOf(H, H), -H], [H, yOf(H, H), H], [-H, yOf(H, H), H]];
  return (
    <>
      {edges.map((pts, i) => <Tube key={`e${i}`} points={pts} r={0.055} />)}
      {ribs.map((pts, i) => <Tube key={`r${i}`} points={pts} r={0.03} mat={{ color: "#8a9198", metalness: 0.7, roughness: 0.5 }} />)}
      {corners.map((c, i) => <Strut key={`s${i}`} from={c} to={[Math.sign(c[0]) * 0.22, F - 0.05, Math.sign(c[2]) * 0.18]} />)}
      <Strut from={[0.22, F - 0.05, -0.18]} to={[H, yOf(H, H) - 0.07, -H]} r={0.045} />
    </>
  );
}

function Receiver({ solar, selected, onSelect, copy }) {
  return (
    <group position={[0, F + 0.08, 0]} onClick={e => { e.stopPropagation(); onSelect("receiver"); }}>
      <mesh>
        <boxGeometry args={[0.62, 0.26, 0.5]} />
        <meshStandardMaterial color="#5f666d" metalness={0.75} roughness={0.4} emissive={selected === "receiver" ? "#00ff88" : "#ff3b30"} emissiveIntensity={0.1 + solar / 100 * 0.7} />
      </mesh>
      <mesh position={[0, 0.17, 0]}>
        <boxGeometry args={[0.5, 0.08, 0.4]} />
        <meshStandardMaterial {...STEEL} />
      </mesh>
      <mesh position={[0, -0.14, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.16, 24]} />
        <meshBasicMaterial color={solar > 0 ? "#ff9c47" : "#4f5559"} side={THREE.DoubleSide} />
      </mesh>
      <pointLight position={[0, -0.3, 0]} intensity={solar / 100 * 8} distance={4} color="#ff6840" />
      <Hotspot part="receiver" position={[0, 0.7, 0]} {...{ selected, onSelect, copy }} />
    </group>
  );
}

function Pedestal() {
  const block = (pos, rotY) => (
    <mesh key={pos.join()} position={pos} rotation={[0, rotY, 0]}>
      <boxGeometry args={[2.7, 0.45, 0.62]} />
      <meshStandardMaterial color="#b5aa96" roughness={1} envMapIntensity={0.25} />
    </mesh>
  );
  const blocks = [
    block([0, 0.225, -0.7], 0), block([0, 0.225, 0.7], 0),
    block([-0.7, 0.675, 0], Math.PI / 2), block([0.7, 0.675, 0], Math.PI / 2),
    block([0, 1.125, -0.6], 0), block([0, 1.125, 0.6], 0),
  ];
  return (
    <group>
      {blocks}
      <mesh position={[0, 1.42, 0]}>
        <boxGeometry args={[0.7, 0.14, 0.7]} />
        <meshStandardMaterial color="#6b7178" metalness={0.6} roughness={0.5} />
      </mesh>
      <mesh position={[0, (1.49 + COL_TOP) / 2, 0]}>
        <cylinderGeometry args={[0.2, 0.22, COL_TOP - 1.49, 24]} />
        <meshStandardMaterial color="#8d949b" metalness={0.8} roughness={0.4} />
      </mesh>
      <mesh position={[0.36, 2.05, 0.05]}>
        <boxGeometry args={[0.28, 0.5, 0.36]} />
        <meshStandardMaterial color="#7a8b9c" metalness={0.4} roughness={0.5} />
      </mesh>
      <mesh position={[0.505, 2.1, 0.05]}>
        <boxGeometry args={[0.02, 0.28, 0.22]} />
        <meshStandardMaterial color="#b9d9f2" emissive="#4f7fa8" emissiveIntensity={0.3} />
      </mesh>
    </group>
  );
}

export default function DiamondSkyModel({ sim, solar, ports, selected, onSelect, copy }) {
  const az = useRef();
  const el = useRef();
  const reflector = useRef();
  const sunRef = useRef();
  const lightRef = useRef();
  const sunDir = useMemo(() => new THREE.Vector3(), []);
  const tmp = useMemo(() => new THREE.Vector3(), []);

  const inGeo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const pos = [];
    RAY_PTS.forEach(([x, z]) => pos.push(x, yOf(x, z) + 4.5, z, x, yOf(x, z) + 0.03, z));
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    return g;
  }, []);
  const reflGeo = useMemo(() => {
    const pos = [];
    RAY_PTS.forEach(([x, z]) => pos.push(x, yOf(x, z) + 0.02, z, 0, F - 0.06, 0));
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    return g;
  }, []);

  useEffect(() => () => { inGeo.dispose(); reflGeo.dispose(); }, [inGeo, reflGeo]);
  useFrame(() => {
    const t = sim.current.time;
    const azA = Math.sin(t * 0.18) * 0.55;
    const elA = 0.98 + Math.sin(t * 0.13) * 0.17;
    sunDir.set(Math.sin(azA) * Math.cos(elA), Math.sin(elA), Math.cos(azA) * Math.cos(elA));
    const center = tmp.set(0, COL_TOP + DISH_LIFT + 1.2, 0);
    sunRef.current.position.copy(center).addScaledVector(sunDir, 7);
    lightRef.current.position.copy(sunRef.current.position);
    az.current.rotation.y = azA;
    el.current.rotation.x = Math.PI / 2 - elA;

    reflector.current.updateWorldMatrix(true, false);
    reflector.current.localToWorld(ports.current.focus.set(0.25, F + 0.08, 0.1));
    reflector.current.localToWorld(ports.current.elbow.set(H, yOf(H, H), H));
  }, -2);

  return (
    <>
      <directionalLight ref={lightRef} intensity={0.4 + solar / 100 * 1.8} color="#fff4dc" />
      <mesh ref={sunRef}>
        <sphereGeometry args={[0.45, 20, 20]} />
        <meshBasicMaterial color={solar > 0 ? "#ffd76a" : "#5b6067"} />
      </mesh>
      <Pedestal />
      <group ref={az} position={[0, COL_TOP, 0]}>
        <mesh rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.1, 0.1, 0.7, 16]} />
          <meshStandardMaterial {...STEEL} />
        </mesh>
        <group ref={el}>
          <mesh position={[0, DISH_LIFT / 2, 0]}>
            <boxGeometry args={[0.5, DISH_LIFT, 0.4]} />
            <meshStandardMaterial color="#6b7178" metalness={0.7} roughness={0.45} />
          </mesh>
          <group ref={reflector} position={[0, DISH_LIFT, 0]}>
            <group onClick={e => { e.stopPropagation(); onSelect("mirrors"); }}><Mirrors selected={selected === "mirrors"} /></group>
            <Frame />
            <Receiver {...{ solar, selected, onSelect, copy }} />
            <lineSegments geometry={inGeo} visible={solar > 0}>
              <lineBasicMaterial color="#fff1b8" transparent opacity={solar / 100 * 0.4} />
            </lineSegments>
            <lineSegments geometry={reflGeo}>
              <lineBasicMaterial color="#ffc94d" transparent opacity={solar / 100 * 0.9} />
            </lineSegments>
          </group>
        </group>
      </group>
      <Hotspot part="mirrors" position={[-2.4, 1.7, 1.4]} {...{ selected, onSelect, copy }} />
    </>
  );
}
